import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { Box, Button, Tooltip } from "@mui/material";
import { useForm, useWatch } from "react-hook-form";
import useScreenSize from "../../../hooks/useScreenSize";
import { useAction } from "../../../hooks/actions";
import { formatAddress } from "../../../helpers/helper";
import { TooltipProps } from "../../../helpers/materialUITheme";
import { getAddressString, getQueryString, getTableHieght } from "../../../helpers/tableHelper";
import {
  useGetFacilityStatusesComplexPostMutation,
  useLazyGetAllFacilitiesQuery,
} from "../../../middleware/facilitiesApi";
import { useGetUserGridSettingQuery, useLazyGetUserGridSettingQuery } from "../../../middleware/usersApi";
import { ModalSelectColumnsServer } from "../../components/Modals/ModalSelectColumnsServer";
import SettingsIcon from "../../components/icons/ButtonIcons/SettingsIcon";
import ClearFilterIcon from "../../components/icons/ButtonIcons/ClearFilterIcon";
import ObjectsHeader from "../ObjectsHeader";
import EquipmentRegistry from "./EquipmentRegistry";
import { getConfigFormModalSelect } from "./form-config";
import ObjectDialog from "./ObjectDialog";
import { formFields } from "./constants";
import { getColumns } from "./columns";
import TableBasic from "./TableBasic";
import { EnhancedTableHead } from "./TableBasic/Filters";
import { getFacilityStatus } from "./helpers";

const ObjectsList = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const screenSize = useScreenSize();
  const [getParams, setSearchParams] = useSearchParams();
  const resetFilters = getParams.has("resetFilters") ? getParams.get("resetFilters") : null;
  const noLinkObjects = getParams.has("noLinkObjects") ? Number(getParams.get("noLinkObjects")) : null;

  const { searchAddressStorage, facilityNameStorage, integrationNumberStorage, phoneNumberStorage, statusStorage } =
    JSON.parse(sessionStorage.getItem("objectListSearchParams")) || {};

  const { control, reset, setValue } = useForm({
    defaultValues: {
      [formFields.searchParams]: {
        [formFields.name]: facilityNameStorage || "",
        [formFields.integrationNumber]: integrationNumberStorage || "",
        [formFields.address]: searchAddressStorage || "",
        [formFields.phoneNumber]: phoneNumberStorage || "",
        [formFields.status]: noLinkObjects || statusStorage || -1,
      },
    },
  });

  const { setIsErrorRequest } = useAction();
  const [openObjectDialog, setOpenObjectDialog] = useState(false);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [equipmentRegistryDialog, setEquipmentRegistryDialog] = useState(false);
  const [openSelectModal, setOpenSelectModal] = useState(false);
  const [objectStatuses, setObjectStatuses] = useState([]);
  const [isPollingActive, setIsPollingActive] = useState(true);
  const [allFacilities, setAllFacilities] = useState({});
  const [resetFlag, setResetFlag] = useState(false);
  const intervalRef = useRef(null);

  const { data: gridColumns } = useGetUserGridSettingQuery({ gridSettingName: "ProtectionObjectList" });
  const [getFacilityStatusesPost] = useGetFacilityStatusesComplexPostMutation();
  const [getUserGrid] = useLazyGetUserGridSettingQuery();
  const [getFacilitiesTable, { error, isLoading }] = useLazyGetAllFacilitiesQuery();

  const isAnyModalOpen = useMemo(() => {
    return openObjectDialog || openSelectModal;
  }, [openObjectDialog, openSelectModal]);

  const params = useWatch({
    control,
    name: formFields.searchParams,
  });

  function writeSearchDataToSessionStorage(newSearchParam, searchValue) {
    const getStorage = JSON.parse(sessionStorage.getItem("objectListSearchParams")) || {};
    const searchParamsForLocalStorage = {
      ...getStorage,
      [newSearchParam]: searchValue,
    };
    sessionStorage.setItem("objectListSearchParams", JSON.stringify(searchParamsForLocalStorage));
  }

  function savePageNumberToSessionStorage(currentPageNumber) {
    const getStorage = JSON.parse(sessionStorage.getItem("objectListSearchParams")) || {};
    const searchParamsForLocalStorage = {
      ...getStorage,
      pageNumberStorage: currentPageNumber,
    };
    sessionStorage.setItem("objectListSearchParams", JSON.stringify(searchParamsForLocalStorage));
  }

  function clearFiltersHandler(event, statusValue = -1) {
    setPageNumber(0);
    reset();
    setSearchParams();
    setValue(`${formFields.searchParams}.${formFields.status}`, statusValue);
    setResetFlag((prev) => !prev);

    sessionStorage.removeItem("objectListSearchParams");
  }

  useEffect(() => {
    const noLinkObjects = getParams.has("noLinkObjects") ? getParams.get("noLinkObjects") : null;
    noLinkObjects && setValue(`${formFields.searchParams}.${formFields.status}`, +noLinkObjects);
    setResetFlag((prev) => !prev);
  }, [location.search, location.hash]);

  const handleOpenObjectDialogState = (id) => {
    if (!openObjectDialog) {
      setOpenObjectDialog(true);
      setSelectedObjectId(id);
      return;
    }
    setOpenObjectDialog(false);
    setSelectedObjectId(null);
  };

  const handleEquipmentRegistryDialog = (id) => {
    if (!equipmentRegistryDialog) {
      setEquipmentRegistryDialog(true);
      setSelectedObjectId(id);
      return;
    }
    setEquipmentRegistryDialog(false);
    setSelectedObjectId(null);
  };

  const columnsFromStorage = useMemo(() => {
    const storageData = gridColumns?.columnSettings || [];
    return storageData?.reduce((acc, { name, visible }) => ({ ...acc, [name]: visible }), {});
  }, [gridColumns]);

  useEffect(() => {
    setIsPollingActive(true);
  }, [params]);

  const columns = useMemo(
    () =>
      getColumns({
        control,
        columnsFromStorage,
        writeSearchDataToSessionStorage,
      }),
    [columnsFromStorage, resetFlag]
  );

  function findElementByFacilityStatusId(targetId, isStatus) {
    const foundElement = objectStatuses.find((item) => {
      return item && item.facilityStatus && item.facilityStatus.id === targetId;
    });
    if (!foundElement) return null;
    const { status, noLinkIntegrNumbers, message, isInTestTime, isInTestUserFullName } = foundElement?.facilityStatus;
    return isStatus
      ? {
          status,
          noLinkIntegrNumbers,
          statusMessage: message,
          isInTestData: {
            isInTestTime,
            isInTestUserFullName,
          },
        }
      : foundElement?.messageChart;
  }

  const getFacilitiesTableData = (isSetFirstPage = false) => {
    isSetFirstPage && setPageNumber(0);
    const resultFilters = {
      ...params,
      address: getAddressString(params?.address, "AddressFilter"),
      status: params.status ? getFacilityStatus(params.status) : -1,
    };

    const queryString = getQueryString(resultFilters);

    getFacilitiesTable({
      filters: queryString,
      pageNumber: pageNumber,
    }).then(({ error, data }) => {
      if (error) return;

      const facilitiesIds = data.data.map((item) => item.id);
      getFacilityStatusesPost(facilitiesIds).then((response) => {
        setAllFacilities(data);
        setObjectStatuses(response.data);
      });
    });
  };

  const recordsCount = useMemo(() => allFacilities && allFacilities?.recordsCount, [allFacilities]);

  const getFacilitiesStatusData = async () => {
    const { data } = allFacilities;
    if (!data || data?.length === 0) return;

    const facilitiesIds = data.map((item) => item.id);
    getFacilityStatusesPost(facilitiesIds).then((response) => {
      setObjectStatuses(response.data);
    });
  };

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (isPollingActive && !isAnyModalOpen) {
      getFacilitiesStatusData();
      intervalRef.current = setInterval(getFacilitiesStatusData, 5000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [allFacilities, isPollingActive, isAnyModalOpen]);

  useEffect(() => {
    if (resetFilters) {
      clearFiltersHandler({}, -1);
      return;
    }
    if (noLinkObjects) {
      clearFiltersHandler({}, noLinkObjects);
      setValue(`${formFields.searchParams}.${formFields.status}`, noLinkObjects);
    }
  }, [resetFilters, noLinkObjects]);

  useEffect(() => {
    if (!params) return;
    getFacilitiesTableData(true);
  }, [params]);

  useEffect(() => {
    if (pageNumber === 0) return;
    getFacilitiesTableData(false);
  }, [pageNumber]);

  useEffect(() => {
    setIsErrorRequest(error);
  }, [error]);

  const handleOpenSelectColumnsModal = () => {
    setOpenSelectModal(true);
  };

  const rows = useMemo(() => {
    const { data } = allFacilities;
    return data?.length && objectStatuses.length
      ? data.map(({ id, name, integrationNumber, address, phoneNumber }) => {
          const formattedAddress = formatAddress(address);
          return {
            id,
            status: findElementByFacilityStatusId(id, true),
            integrationNumber,
            phoneNumber,
            name: {
              name: `${name}`,
              action: () => handleOpenObjectDialogState(id),
            },
            address: formattedAddress,
            messages: findElementByFacilityStatusId(id, false),
          };
        })
      : [];
  }, [allFacilities, objectStatuses]);

  const renderHeader = useMemo(() => <EnhancedTableHead columns={columns} />, [columns]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <ObjectsHeader />

      <Box sx={{ display: "flex", gap: "8px", justifyContent: "end" }}>
        <Tooltip title="Очистить все фильтры" componentsProps={TooltipProps} placement="bottom" arrow>
          <Button className="btn btn-white" onClick={clearFiltersHandler}>
            <ClearFilterIcon />
          </Button>
        </Tooltip>
        <Tooltip
          title={t("button:customize_fields_to_display")}
          componentsProps={TooltipProps}
          placement="bottom"
          arrow
        >
          <Button className="btn btn-white" onClick={handleOpenSelectColumnsModal}>
            <SettingsIcon />
          </Button>
        </Tooltip>
      </Box>

      <TableBasic
        renderHeader={renderHeader}
        columns={columns}
        rows={rows}
        handleClickCell={() => {}}
        withSearchField={false}
        isLoading={isLoading}
        pageNumber={pageNumber}
        changePageNumber={setPageNumber}
        count={recordsCount}
        minHeight={getTableHieght(screenSize, false, "390px").min}
        maxHeight={getTableHieght(screenSize, false, "390px").max}
        savePageNumberToSessionStorage={savePageNumberToSessionStorage}
        setIsPollingActive={setIsPollingActive}
      />

      {openObjectDialog && (
        <ObjectDialog open={openObjectDialog} onClose={handleOpenObjectDialogState} objectId={selectedObjectId} />
      )}

      {equipmentRegistryDialog && (
        <EquipmentRegistry
          open={equipmentRegistryDialog}
          onClose={handleEquipmentRegistryDialog}
          objectId={selectedObjectId}
        />
      )}

      {openSelectModal && (
        <ModalSelectColumnsServer
          open={openSelectModal}
          setOpen={setOpenSelectModal}
          data={columnsFromStorage}
          refetch={() => getUserGrid({ gridSettingName: "ProtectionObjectList" })}
          nameStorage={"ProtectionObjectList"}
          config={getConfigFormModalSelect(gridColumns?.columnSettings || [])}
        />
      )}
    </Box>
  );
};

export default ObjectsList;
